import { RANK_ORDER } from '../types/network'
import type {
  ApplicationStatus,
  CapturedEvidence,
  DocumentReviewState,
  EarningEntry,
  EarningStatus,
  EvidenceRequirement,
  FieldTask,
  PerformanceEvent,
  RepRank,
  RepStatus,
  RewardRule,
  TaskStatus,
  VerificationSubmission,
} from '../types/network'
import type { BadgeTone } from '../components/ui/Badge'

/* -------------------------------------------------- authorisation scopes -- */

/**
 * What a rank is authorised to do, and what it is explicitly not. Rendering the
 * restricted half matters as much as the permitted half: it is what stops the
 * hierarchy being decorative, and it is what prevents role escalation reading as
 * an oversight rather than a decision.
 */
export interface AuthorisationScope {
  permitted: string[]
  restricted: string[]
}

const BASE_PERMITTED = [
  'Complete assigned tasks',
  'Submit evidence',
  'Submit campus intelligence',
  'Create landlord and business leads',
  'Refer students',
  'Report incidents',
]

const NEVER: string[] = [
  'Approve your own verification',
  'Approve an earning or a payout',
  'Approve a representative application',
  'Change a performance score',
  'Act on a campus you are not assigned to',
]

export function scopeFor(rank: RepRank): AuthorisationScope {
  const index = RANK_ORDER.indexOf(rank)
  const permitted = [...BASE_PERMITTED]
  const restricted = [...NEVER]

  if (index >= 1) permitted.push('Mentor representatives in your area')
  else restricted.push('Mentor or review other representatives')

  if (index >= 2) permitted.push('See representatives assigned to you', 'Distribute tasks within your area', 'Monitor area performance')
  else restricted.push('See other representatives’ work', 'Distribute tasks')

  if (index >= 3) permitted.push('Manage the campus team and areas', 'See campus-level analytics', 'Appoint area leads')
  else restricted.push('Manage a campus team', 'See campus-level analytics')

  if (index >= 4) permitted.push('Oversee campuses in your region')
  else restricted.push('Act across campuses')

  return { permitted, restricted }
}

/* ------------------------------------------------------------------ tones -- */

export function taskTone(status: TaskStatus): BadgeTone {
  if (status === 'Approved') return 'ok'
  if (status === 'Rejected' || status === 'Expired') return 'danger'
  if (status === 'Rework required' || status === 'Under review' || status === 'Submitted') return 'warn'
  if (status === 'Open') return 'neutral'
  return 'brand'
}

export function applicationTone(status: ApplicationStatus): BadgeTone {
  if (status === 'Approved') return 'ok'
  if (status === 'Rejected' || status === 'Withdrawn') return 'danger'
  if (status === 'Waitlisted' || status === 'Under review' || status === 'Interview') return 'warn'
  if (status === 'Shortlisted') return 'brand'
  return 'neutral'
}

export function documentTone(state: DocumentReviewState): BadgeTone {
  if (state === 'Verified') return 'ok'
  if (state === 'Rejected') return 'danger'
  if (state === 'Re-upload required') return 'warn'
  if (state === 'Not provided') return 'neutral'
  return 'brand'
}

export function repTone(status: RepStatus): BadgeTone {
  if (status === 'Active') return 'ok'
  if (status === 'Suspended' || status === 'Terminated') return 'danger'
  if (status === 'Probation' || status === 'Pending onboarding') return 'warn'
  return 'neutral'
}

export function earningTone(status: EarningStatus): BadgeTone {
  if (status === 'Paid' || status === 'Approved') return 'ok'
  if (status === 'Rejected' || status === 'Reversed') return 'danger'
  if (status === 'Disputed' || status === 'Under review') return 'warn'
  return 'neutral'
}

/* ------------------------------------------------------------ evidence gate -- */

/** Which configured evidence requirements a submission does not yet satisfy. */
export function missingEvidence(
  requirements: EvidenceRequirement[],
  captured: CapturedEvidence,
): EvidenceRequirement[] {
  return requirements.filter((requirement) => {
    if (!requirement.required) return false
    if (requirement.kind === 'gps') return captured.gps === null
    if (requirement.kind === 'photo' || requirement.kind === 'video') {
      return !captured.photos.some((photo) => photo.key === requirement.key)
    }
    if (requirement.kind === 'declaration') return !captured.declaration
    return (captured.fields[requirement.key] ?? '').trim().length === 0
  })
}

export function canSubmit(task: FieldTask, captured: CapturedEvidence): boolean {
  return missingEvidence(task.evidence, captured).length === 0
}

/* ------------------------------------------------------------- geolocation -- */

const EARTH_RADIUS_M = 6_371_000

export function metresBetween(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const toRad = (value: number) => (value * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return Math.round(2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h)))
}

/** Verification radius before a submission is flagged for human review. */
export const VERIFICATION_RADIUS_M = 250

/**
 * Anomalies are flagged, never auto-rejected. A reviewer decides.
 */
export function evidenceFlags(
  captured: CapturedEvidence,
  distanceM: number | null,
  priorHashes: string[],
): string[] {
  const flags: string[] = []
  if (!captured.gps) {
    flags.push('Location unavailable at capture — reviewer should confirm the address another way')
  } else if (captured.gps.accuracyM > 120) {
    flags.push(`Low location accuracy (±${captured.gps.accuracyM}m)`)
  }
  if (distanceM !== null && distanceM > VERIFICATION_RADIUS_M) {
    flags.push(
      `Captured ${distanceM}m from the expected property location, outside the ${VERIFICATION_RADIUS_M}m radius`,
    )
  }
  const duplicate = captured.photos.find((photo) => priorHashes.includes(photo.dataUrl))
  if (duplicate) {
    flags.push('Media matches evidence already submitted elsewhere on the network')
  }
  if (captured.photos.length > 0 && captured.gps) {
    const stale = captured.photos.some(
      (photo) => Math.abs(Date.parse(photo.at) - Date.parse(captured.gps!.at)) > 45 * 60 * 1000,
    )
    if (stale) flags.push('Media timestamp is more than 45 minutes from the location capture')
  }
  return flags
}

/* -------------------------------------------------------------- reputation -- */

export interface ScoreWeights {
  accuracy: number
  evidenceQuality: number
  completion: number
  reliability: number
  conduct: number
}

/** Configurable weights. Representatives cannot edit these or their own score. */
export const DEFAULT_WEIGHTS: ScoreWeights = {
  accuracy: 35,
  evidenceQuality: 20,
  completion: 20,
  reliability: 15,
  conduct: 10,
}

export interface ScoreBreakdown {
  score: number
  parts: { label: string; weight: number; achieved: number; note: string }[]
  strengths: string[]
  improve: string[]
}

export function scoreFor(
  events: PerformanceEvent[],
  submissions: VerificationSubmission[],
  tasks: FieldTask[],
  weights: ScoreWeights = DEFAULT_WEIGHTS,
): ScoreBreakdown {
  const decided = submissions.filter((item) =>
    ['Approved', 'Rejected', 'Rework required'].includes(item.status),
  )
  const approved = decided.filter((item) => item.status === 'Approved').length
  const accuracy = decided.length === 0 ? null : approved / decided.length

  const flagged = submissions.filter((item) => item.flags.length > 0).length
  const quality = submissions.length === 0 ? null : 1 - flagged / submissions.length

  const closed = tasks.filter((task) =>
    ['Approved', 'Rejected', 'Expired'].includes(task.status),
  )
  const completion =
    closed.length === 0
      ? null
      : closed.filter((task) => task.status === 'Approved').length / closed.length

  const late = events.filter((event) => event.kind === 'Task completed late').length
  const reliability = tasks.length === 0 ? null : Math.max(0, 1 - late / Math.max(tasks.length, 1))

  const warnings = events.filter((event) => event.kind === 'Policy warning').length
  const conduct = Math.max(0, 1 - warnings * 0.34)

  const parts = [
    {
      label: 'Verification accuracy',
      weight: weights.accuracy,
      achieved: accuracy,
      note:
        decided.length === 0
          ? 'No reviewed submissions yet'
          : `${approved} of ${decided.length} submissions approved`,
    },
    {
      label: 'Evidence quality',
      weight: weights.evidenceQuality,
      achieved: quality,
      note:
        submissions.length === 0
          ? 'No submissions yet'
          : `${flagged} of ${submissions.length} raised a review flag`,
    },
    {
      label: 'Task completion',
      weight: weights.completion,
      achieved: completion,
      note: closed.length === 0 ? 'No closed tasks yet' : `${closed.length} tasks closed`,
    },
    {
      label: 'Reliability',
      weight: weights.reliability,
      achieved: reliability,
      note: late === 0 ? 'Nothing submitted late' : `${late} late submission(s)`,
    },
    {
      label: 'Conduct',
      weight: weights.conduct,
      achieved: conduct,
      note: warnings === 0 ? 'No policy warnings' : `${warnings} policy warning(s)`,
    },
  ]

  const scored = parts.filter((part) => part.achieved !== null)
  const availableWeight = scored.reduce((total, part) => total + part.weight, 0)
  const score =
    availableWeight === 0
      ? 0
      : Math.round(
          scored.reduce((total, part) => total + part.weight * (part.achieved as number), 0) /
            availableWeight *
            100,
        )

  return {
    score,
    parts: parts.map((part) => ({
      label: part.label,
      weight: part.weight,
      achieved: part.achieved === null ? -1 : Math.round(part.achieved * 100),
      note: part.note,
    })),
    strengths: parts
      .filter((part) => part.achieved !== null && (part.achieved as number) >= 0.9)
      .map((part) => part.label),
    improve: parts
      .filter((part) => part.achieved !== null && (part.achieved as number) < 0.75)
      .map((part) => part.label),
  }
}

/* -------------------------------------------------------------- promotion -- */

export interface PromotionCriterion {
  label: string
  met: boolean
  detail: string
}

export function nextRank(rank: RepRank): RepRank | null {
  const index = RANK_ORDER.indexOf(rank)
  return index === -1 || index === RANK_ORDER.length - 1 ? null : RANK_ORDER[index + 1]
}

export function rankAtLeast(rank: RepRank, minimum: RepRank): boolean {
  return RANK_ORDER.indexOf(rank) >= RANK_ORDER.indexOf(minimum)
}

/* --------------------------------------------------------------- earnings -- */

export function netAmount(entry: EarningEntry): number {
  return entry.grossAmount + entry.adjustment
}

/** Balances are derived from the ledger, never stored as a mutable total. */
export function balancesFor(entries: EarningEntry[]) {
  const live = entries.filter((entry) => entry.status !== 'Reversed')
  const sum = (statuses: EarningStatus[]) =>
    live
      .filter((entry) => statuses.includes(entry.status))
      .reduce((total, entry) => total + netAmount(entry), 0)

  const reversed = entries
    .filter((entry) => entry.status === 'Reversed')
    .reduce((total, entry) => total + netAmount(entry), 0)

  return {
    pending: sum(['Pending', 'Under review']),
    approved: sum(['Approved']),
    paid: sum(['Paid']),
    disputed: sum(['Disputed']),
    reversed,
    lifetime: sum(['Approved', 'Paid']),
  }
}

export function ruleAmount(rule: RewardRule, basis = 0): number {
  if (rule.percentage !== undefined) return Math.round((basis * rule.percentage) / 100)
  return rule.amount
}

export const MINIMUM_PAYOUT = 5000

/* ------------------------------------------------------------------- misc -- */

export function stamp(offsetMinutes = 0): string {
  const base = new Date('2026-08-14T09:00:00+01:00')
  base.setMinutes(base.getMinutes() + offsetMinutes)
  return base.toISOString()
}

export function shortTime(iso: string): string {
  const date = new Date(iso)
  return date.toLocaleString('en-NG', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function id(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`
}
